import React, { useEffect, useState } from "react";

import { Typography } from "@packages/ds";
import { useIntl, FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import Error from "Components/Error";
import { Button, ButtonWrapper } from "ds/Button";
import Checkbox from "ds/Checkbox";
import * as Code from "ds/Code";
import Modal from "ds/Modal";
import * as Sticker from "ds/Sticker";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { goToEnvironment, capitalize } from "Libs/utils";
import { environmentLoadingSelector } from "Reducers/environment";
import {
  deploy,
  deployStateSelector
} from "Reducers/environment/actions/deploy";
import {
  redeploy,
  redeployStateSelector
} from "Reducers/environment/actions/redeploy";
import { useDeploymentType } from "Reducers/project/project/hooks/useDeploymentType";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import * as S from "./deploy.styles";

const Deploy = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { organizationId, projectId, environmentId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    environmentId: string;
  }>();

  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isRedeployChecked, setIsRedeployChecked] = useState(false);
  const {
    isLoading: isDeployLoading,
    error: deployError,
    data: deployData
  } = useAppSelector(deployStateSelector);
  const {
    isLoading: isRedeployLoading,
    error: redeployError,
    data: redeployData
  } = useAppSelector(redeployStateSelector);

  const data = redeployData || deployData;
  const error = redeployError || deployError;

  const isEnvironmentLoading = useSelectorWithUrlParams(
    environmentLoadingSelector,
    {
      organizationId,
      projectId,
      environmentId
    }
  );
  const {
    shouldShowDeploymentType,
    isAutomaticDeploymentEnabled,
    hasStagedActivities
  } = useDeploymentType();
  const isUpToDate =
    (shouldShowDeploymentType &&
      !isAutomaticDeploymentEnabled &&
      !hasStagedActivities) ||
    false;

  useEffect(() => {
    setIsRedeployChecked(isUpToDate);
  }, [setIsRedeployChecked, isUpToDate]);

  const onClose = (): void => {
    goToEnvironment(navigate, organizationId, projectId, environmentId);
  };

  if (data && !isRedirecting) {
    setIsRedirecting(true);
    onClose();
  }

  const commandName = intl.formatMessage({
    id: "CLI_command_name"
  });

  const command = intl.formatMessage(
    { id: isRedeployChecked ? "redeploy.command" : "deploy.command" },
    {
      commandName
    }
  );
  const handleActionChange = (value: boolean): void => {
    setIsRedeployChecked(value);
  };
  const onStartClick = () => {
    const methodDeploy = isRedeployChecked ? redeploy : deploy;
    dispatch(methodDeploy({ organizationId, projectId, environmentId }));
    onClose();
  };

  return (
    <Modal
      id="deploy-modal"
      isOpen={true}
      size="medium"
      onOpenChange={onClose}
      handleClose={onClose}
    >
      <Typography.Title tag="h3">
        {intl.formatMessage({ id: "deploy.modal.title" })}
      </Typography.Title>
      <div>
        {error?.message && <Error>{error.message}</Error>}
        <Typography.BodyText>
          <FormattedMessage id="deploy.modal.text" />
        </Typography.BodyText>
        <S.RedeployWrapper>
          {isUpToDate && (
            <S.StickerRoot priority="information">
              <Sticker.Icon priority="information" />
              <Sticker.Text>
                {intl.formatMessage({ id: "deploy.sticker.text" })}
              </Sticker.Text>
            </S.StickerRoot>
          )}
          <Checkbox
            label={`${intl.formatMessage({ id: "redeploy" })}`}
            checked={isRedeployChecked}
            disabled={isUpToDate}
            aria-disabled={isUpToDate}
            onCheckedChanged={handleActionChange}
            forId="super-user"
            id="access.project_admin"
          />
          <S.RedeployText isGrayed={isUpToDate}>
            {intl.formatMessage({ id: "deploy.redeploy_checkbox.description" })}
          </S.RedeployText>
        </S.RedeployWrapper>

        <S.Separator />

        <S.CLI weight="semiBold">
          {intl.formatMessage({
            id: "deploy.cli",
            defaultMessage: "CLI comand:"
          })}
        </S.CLI>

        <Code.Block isCopyable singleLine id={command}>
          {command}
        </Code.Block>

        {isEnvironmentLoading || isDeployLoading || isRedeployLoading ? (
          <S.LoadingSuccess />
        ) : (
          <ButtonWrapper spacing="modal" justifyContent="end">
            <Button
              analyticId="cancel"
              id="environment-deploy-cancel-btn"
              variant="secondary"
              onClick={onClose}
            >
              {capitalize(intl.formatMessage({ id: "cancel" }))}
            </Button>
            <Button
              analyticId="deploy.button"
              id="environment-deploy-btn"
              onClick={onStartClick}
            >
              {capitalize(intl.formatMessage({ id: "deploy.button" }))}
            </Button>
          </ButtonWrapper>
        )}
      </div>
    </Modal>
  );
};

export default Deploy;
